/**
 * Nutrition Tab Component
 *
 * Displays and allows editing of allergens and nutritional information.
 */

import { Check, AlertTriangle } from 'lucide-react';
import { EditableField } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { RawProductWithRelations } from '@bstock/shared';

const COMMON_ALLERGENS = [
  'Gluten',
  'Crustaces',
  'Oeufs',
  'Poisson',
  'Arachides',
  'Soja',
  'Lait',
  'Fruits a coque',
  'Celeri',
  'Moutarde',
  'Sesame',
  'Sulfites',
  'Lupin',
  'Mollusques',
];

interface NutritionTabProps {
  product: RawProductWithRelations;
  onChange: (field: string, value: unknown) => void;
}

export function NutritionTab({ product, onChange }: NutritionTabProps) {
  const allergens = product.allergens ?? [];
  const nutritionalInfo = product.nutritionalInfo as Record<string, number> | null;

  const toggleAllergen = (allergen: string) => {
    const updated = allergens.includes(allergen)
      ? allergens.filter((a) => a !== allergen)
      : [...allergens, allergen];
    onChange('allergens', updated);
  };

  const updateNutrition = (key: string, value: string, maxValue: number) => {
    if (!value) {
      const updated = { ...(nutritionalInfo ?? {}) };
      delete updated[key];
      onChange('nutritionalInfo', Object.keys(updated).length > 0 ? updated : null);
      return;
    }

    // Parse as integer, clamp between 0 and max
    let numValue = Math.floor(parseFloat(value));
    if (isNaN(numValue) || numValue < 0) numValue = 0;
    if (numValue > maxValue) numValue = maxValue;

    const updated = { ...(nutritionalInfo ?? {}), [key]: numValue };
    onChange('nutritionalInfo', updated);
  };

  return (
    <div className="space-y-6">
      {/* Allergenes */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-warning-500" />
          Allergenes
        </h3>

        {allergens.length > 0 && (
          <p className="text-sm text-warning-600 mb-4">
            {allergens.length} allergene{allergens.length > 1 ? 's' : ''} declare{allergens.length > 1 ? 's' : ''}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {COMMON_ALLERGENS.map((allergen) => {
            const isSelected = allergens.includes(allergen);
            return (
              <label
                key={allergen}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-warning-50 border border-warning-300'
                    : 'bg-neutral-50 hover:bg-neutral-100'
                )}
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleAllergen(allergen)}
                    className="sr-only"
                  />
                  <div
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                      isSelected
                        ? 'border-warning-500 bg-warning-500'
                        : 'border-neutral-300 bg-white'
                    )}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-dark-500">{allergen}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Informations nutritionnelles */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-1">
          Informations nutritionnelles
        </h3>
        <p className="text-sm text-neutral-500 mb-4">Pour 100g de produit</p>

        <div className="grid grid-cols-2 gap-4">
          {[
            { key: 'calories', label: 'Energie (kcal)', unit: 'kcal', max: 10000 },
            { key: 'proteins', label: 'Proteines', unit: 'g', max: 100 },
            { key: 'carbs', label: 'Glucides', unit: 'g', max: 100 },
            { key: 'fats', label: 'Lipides', unit: 'g', max: 100 },
            { key: 'fiber', label: 'Fibres', unit: 'g', max: 100 },
            { key: 'salt', label: 'Sel', unit: 'g', max: 100 },
          ].map(({ key, label, unit, max }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                {label}
              </label>
              <EditableField
                value={nutritionalInfo?.[key]?.toString() ?? ''}
                onSave={(value) => updateNutrition(key, value, max)}
                type="number"
                placeholder="0"
              />
              <p className="text-xs text-neutral-500 mt-1">{unit}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
